/**
 * Formula Parser and Evaluator.
 *
 * Parses and evaluates mathematical formulas where variables are event names.
 *
 * Supported syntax:
 *   - Arithmetic operators: +, -, *, /
 *   - Parentheses for grouping
 *   - Variables = event names (alphanumeric + underscore, must start with letter or _)
 *   - Numeric literals (integers and decimals)
 *
 * Example:
 *   formula: "any_purchase - (initial_purchase + renewal)"
 *   values:  { any_purchase: 100, initial_purchase: 60, renewal: 20 }
 *   result:  20
 *
 * Implementation: Recursive descent parser (tokenize → parse → evaluate).
 */

// ─── Token Types ────────────────────────────────────────────────────────────

type TokenType = 'NUMBER' | 'IDENT' | 'PLUS' | 'MINUS' | 'STAR' | 'SLASH' | 'LPAREN' | 'RPAREN' | 'EOF';

interface Token {
  type: TokenType;
  value: string;
}

// ─── Tokenizer ──────────────────────────────────────────────────────────────

function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < formula.length) {
    const ch = formula[i];

    // Skip whitespace
    if (/\s/.test(ch)) {
      i++;
      continue;
    }

    // Number literal
    if (/\d/.test(ch) || (ch === '.' && i + 1 < formula.length && /\d/.test(formula[i + 1]))) {
      let num = '';
      while (i < formula.length && (/\d/.test(formula[i]) || formula[i] === '.')) {
        num += formula[i];
        i++;
      }
      tokens.push({ type: 'NUMBER', value: num });
      continue;
    }

    // Identifier (event name)
    if (/[a-zA-Z_]/.test(ch)) {
      let ident = '';
      while (i < formula.length && /\w/.test(formula[i])) {
        ident += formula[i];
        i++;
      }
      tokens.push({ type: 'IDENT', value: ident });
      continue;
    }

    // Operators and parens
    switch (ch) {
      case '+': tokens.push({ type: 'PLUS', value: '+' }); break;
      case '-': tokens.push({ type: 'MINUS', value: '-' }); break;
      case '*': tokens.push({ type: 'STAR', value: '*' }); break;
      case '/': tokens.push({ type: 'SLASH', value: '/' }); break;
      case '(': tokens.push({ type: 'LPAREN', value: '(' }); break;
      case ')': tokens.push({ type: 'RPAREN', value: ')' }); break;
      default:
        throw new Error(`Unexpected character '${ch}' at position ${i}`);
    }
    i++;
  }

  tokens.push({ type: 'EOF', value: '' });
  return tokens;
}

// ─── Parser (Recursive Descent) ─────────────────────────────────────────────

class Parser {
  private tokens: Token[];
  private pos: number;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
    this.pos = 0;
  }

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private consume(expectedType?: TokenType): Token {
    const token = this.tokens[this.pos];
    if (expectedType && token.type !== expectedType) {
      throw new Error(`Expected ${expectedType} but got ${token.type} ('${token.value}')`);
    }
    this.pos++;
    return token;
  }

  /**
   * Parse an expression.
   * Grammar:
   *   expr   → term (('+' | '-') term)*
   *   term   → unary (('*' | '/') unary)*
   *   unary  → ('-')? factor
   *   factor → NUMBER | IDENT | '(' expr ')'
   */
  parseExpression(): ASTNode {
    let left = this.parseTerm();

    while (this.peek().type === 'PLUS' || this.peek().type === 'MINUS') {
      const op = this.consume().value as '+' | '-';
      const right = this.parseTerm();
      left = { type: 'binary', op, left, right };
    }

    return left;
  }

  private parseTerm(): ASTNode {
    let left = this.parseUnary();

    while (this.peek().type === 'STAR' || this.peek().type === 'SLASH') {
      const op = this.consume().value as '*' | '/';
      const right = this.parseUnary();
      left = { type: 'binary', op, left, right };
    }

    return left;
  }

  private parseUnary(): ASTNode {
    if (this.peek().type === 'MINUS') {
      this.consume();
      const operand = this.parseFactor();
      return { type: 'unary', op: '-', operand };
    }
    if (this.peek().type === 'PLUS') {
      this.consume(); // Unary plus — no-op
    }
    return this.parseFactor();
  }

  private parseFactor(): ASTNode {
    const token = this.peek();

    if (token.type === 'NUMBER') {
      this.consume();
      return { type: 'number', value: parseFloat(token.value) };
    }

    if (token.type === 'IDENT') {
      this.consume();
      return { type: 'variable', name: token.value };
    }

    if (token.type === 'LPAREN') {
      this.consume('LPAREN');
      const expr = this.parseExpression();
      this.consume('RPAREN');
      return expr;
    }

    throw new Error(`Unexpected token '${token.value}' (${token.type})`);
  }

  parse(): ASTNode {
    const ast = this.parseExpression();
    if (this.peek().type !== 'EOF') {
      throw new Error(`Unexpected token '${this.peek().value}' after expression`);
    }
    return ast;
  }
}

// ─── AST Types ──────────────────────────────────────────────────────────────

type ASTNode =
  | { type: 'number'; value: number }
  | { type: 'variable'; name: string }
  | { type: 'binary'; op: '+' | '-' | '*' | '/'; left: ASTNode; right: ASTNode }
  | { type: 'unary'; op: '-'; operand: ASTNode };

// ─── Evaluator ──────────────────────────────────────────────────────────────

function evaluateAST(node: ASTNode, values: Record<string, number>): number {
  switch (node.type) {
    case 'number':
      return node.value;

    case 'variable': {
      const val = values[node.name];
      if (val === undefined) {
        return 0; // Missing event → treat as 0
      }
      return val;
    }

    case 'unary':
      return -evaluateAST(node.operand, values);

    case 'binary': {
      const left = evaluateAST(node.left, values);
      const right = evaluateAST(node.right, values);
      switch (node.op) {
        case '+': return left + right;
        case '-': return left - right;
        case '*': return left * right;
        case '/': return right === 0 ? 0 : left / right; // Avoid division by zero
      }
    }
  }
}

// ─── Collect Variables ──────────────────────────────────────────────────────

function collectVariables(node: ASTNode, vars: Set<string>): void {
  switch (node.type) {
    case 'number':
      break;
    case 'variable':
      vars.add(node.name);
      break;
    case 'unary':
      collectVariables(node.operand, vars);
      break;
    case 'binary':
      collectVariables(node.left, vars);
      collectVariables(node.right, vars);
      break;
  }
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Extract unique event names referenced in a formula.
 *
 * @example
 * extractEvents("any_purchase - (initial_purchase + renewal)")
 * // → ["any_purchase", "initial_purchase", "renewal"]
 */
export function extractEvents(formula: string): string[] {
  try {
    const tokens = tokenize(formula);
    const parser = new Parser(tokens);
    const ast = parser.parse();
    const vars = new Set<string>();
    collectVariables(ast, vars);
    return Array.from(vars);
  } catch {
    // If parsing fails, fall back to regex extraction
    const matches = formula.match(/[a-zA-Z_]\w*/g);
    return matches ? [...new Set(matches)] : [];
  }
}

/**
 * Evaluate a formula by substituting event values and computing the result.
 * Missing events are treated as 0.
 *
 * @example
 * evaluateFormula("any_purchase - (initial_purchase + renewal)", {
 *   any_purchase: 100, initial_purchase: 60, renewal: 20
 * })
 * // → 20
 */
export function evaluateFormula(formula: string, values: Record<string, number>): number {
  const tokens = tokenize(formula);
  const parser = new Parser(tokens);
  const ast = parser.parse();
  const result = evaluateAST(ast, values);
  return Math.round(result * 100) / 100; // Round to 2 decimal places
}

/**
 * Validate a formula's syntax without evaluating it.
 *
 * Uses two-tier validation:
 *   1. **Strict** – full parse succeeds → `valid: true`
 *   2. **Lenient** – strip trailing operators / open parens (user still
 *      typing) and retry. If the stripped version parses → `valid: false`
 *      but with a soft "incomplete" hint instead of a scary parser error.
 *
 * This prevents the validation badge from flashing red every time the user
 * types an operator like `+`, `-`, etc.
 *
 * @returns Object with `valid` boolean and optional `error` message.
 */
export function validateFormula(formula: string): { valid: boolean; error?: string } {
  if (!formula || !formula.trim()) {
    return { valid: false, error: 'Formula is empty' };
  }

  // 1. Try strict parse
  try {
    const tokens = tokenize(formula);
    const parser = new Parser(tokens);
    parser.parse();
    return { valid: true };
  } catch {
    // continue to lenient check
  }

  // 2. Strip trailing operators / whitespace / open-parens and retry.
  //    This handles partial expressions like "event1 +" or "event1 * ("
  const trimmed = formula.replace(/[\s+\-*/(\s]+$/, '').trim();
  if (trimmed.length > 0) {
    try {
      const tokens = tokenize(trimmed);
      const parser = new Parser(tokens);
      parser.parse();
      // The core expression is valid, user is just mid-keystroke
      return { valid: false, error: 'Keep typing…' };
    } catch {
      // Still broken even after trimming → real syntax error
    }
  }

  // 3. Final fallback — report the real error from a strict parse
  try {
    const tokens = tokenize(formula);
    const parser = new Parser(tokens);
    parser.parse();
    return { valid: true };
  } catch (err) {
    return {
      valid: false,
      error: err instanceof Error ? err.message : 'Invalid formula',
    };
  }
}
